import React, { useState, useMemo, useEffect } from 'react';
import { Segmented, AutoComplete } from 'antd';
import { IMaskInput } from 'react-imask';
import { useTheme } from '../header/Header';
import { correctNumber } from '../crm/feedback';
import { fetchRequestFeedback } from '../../api/crm/feedback';
import { authGoogle, authYandex, logout } from '../header/Header';
import { AlertNoCancel, defenseFromManyRequest } from '../crm/feedback';
import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import { Model } from "../utils/Model3d";
import '../../css/silos/silosConfigurator.css';
import '../../css/silos/axlesConfigurator.css';

// ------------------------------------------------------------
// 1. ДАННЫЕ МОДЕЛЕЙ И ПОЯСНЕНИЯ
// ------------------------------------------------------------
const catalog = {
    AB09: {
        W0: { heights: ['037', '045', '049'], bearings: ['TB', 'BB'] },
        S1: { heights: ['037', '045', '049'], bearings: ['TB', 'BB'] },
        S2: { heights: ['037', '045', '049'], bearings: ['TB', 'BB'] },
        R0: { heights: ['037', '045', '049'], bearings: ['TB', 'BB'] },
    },
    AD09: {
        W0: { heights: ['037', '045', '049'], bearings: ['TB', 'BB'] },
        S1: { heights: ['037', '045', '049'], bearings: ['TB', 'BB'] },
    },
};

const brakeDesc = {
    AB: 'Оснащена барабанным тормозом (AB), обеспечивающим высокую эффективность торможения даже при повышенной нагрузке',
    AD: 'Дисковый тормоз (AD) обеспечивает высокую эффективность торможения, улучшенное теплоотведение и меньший износ в сравнении с барабанными тормозами',
};

const brakeSystems = [
    { id: 'AB', label: 'Барабанный (AB)', desc: brakeDesc.AB },
    { id: 'AD', label: 'Дисковый (AD)', desc: brakeDesc.AD },
];

const springTypes = {
    AB: ['W0', 'S1', 'S2', 'R0'],
    AD: ['W0', 'S1'],
};

const springDesc = {
    W0: 'Сварная полурессора (W0) характеризуется высокой прочностью, что позволяет выдерживать значительные нагрузки и улучшает надёжность подвески',
    S1: 'Кованая однолистовая полурессора (S1) обеспечивает оптимальное распределение нагрузки, снижая общий вес конструкции',
    S2: 'Кованая двухлистовая полурессора (S2) обладает увеличенной прочностью, что делает её идеальной для тяжёлых нагрузок',
    R0: 'Рессорная подвеска (R0) — классическая многолистовая конструкция, обеспечивающая высокую грузоподъёмность и надёжность в тяжёлых условиях эксплуатации',
};

const springLabels = {
    W0: 'Сварная',
    S1: 'Кованая однолистовая',
    S2: 'Кованая двухлистовая',
    R0: 'Рессорная',
};

const bearingDesc = {
    TB: 'Радиально-упорный подшипник (TB), минимизирующий износ и повышающий надежность конструкции',
    BB: 'Блок-подшипник "ЕПК" с размерами Ø90xØ160x125 (BB) обеспечивает компактность, точность работы и устойчивость к износу',
};

const bearingLabels = {
    TB: 'Радиально-упорный',
    BB: 'Блок-подшипник',
};

// Маппинг кодов моделей на параметры 3D-модели
const modelMap = {
    'AB09W0-037-TB': { models: ['AB09W0-037-TB.glb'], styles: { backgroundColor: '#e3e5e6' }, camera: { p: [89.11, 42.08, 66.11], r: [-0.55, 0.82, 0.43] } },
    'AB09W0-037-BB': { models: ['AB09W0-037-BB.glb'], styles: { backgroundColor: '#e3e5e6' }, camera: { p: [89.11, 42.08, 66.11], r: [-0.55, 0.82, 0.43] } },
    'AB09S1-037-TB': { models: ['AB09S1-037-TB.glb'], styles: { backgroundColor: '#a7a3a3' }, camera: { p: [89.11, 42.08, 66.11], r: [-0.55, 0.82, 0.43] } },
    'AB09S1-037-BB': { models: ['AB09S1-037-BB.glb'], styles: { backgroundColor: '#a7a3a3' }, camera: { p: [89.11, 42.08, 66.11], r: [-0.55, 0.82, 0.43] } },
    'AB09S2-037-TB': { models: ['AB09S2-037-TB.glb'], styles: { backgroundColor: '#a7a3a3' }, camera: { p: [89.11, 42.08, 66.11], r: [-0.55, 0.82, 0.43] } },
    'AB09S2-037-BB': { models: ['AB09S2-037-BB.glb'], styles: { backgroundColor: '#a7a3a3' }, camera: { p: [89.11, 42.08, 66.11], r: [-0.55, 0.82, 0.43] } },
    'AD09W0-037-TB': { models: ['AD09W0-037-TB.glb'], styles: { backgroundColor: '#e3e5e6' }, camera: { p: [89.11, 42.08, 66.11], r: [-0.55, 0.82, 0.43] } },
    'AD09W0-037-TB-noAbsorber': { models: ['AD09W0-037-TB-noAbsorber.glb'], styles: { backgroundColor: '#e3e5e6' }, camera: { p: [89.11, 42.08, 66.11], r: [-0.55, 0.82, 0.43] } },
    'AB09W0-045-TB': { models: ['AB09W0-037-TB.glb'], styles: { backgroundColor: '#e3e5e6' }, camera: { p: [89.11, 42.08, 66.11], r: [-0.55, 0.82, 0.43] } },
    'AB09W0-045-BB': { models: ['AB09W0-037-BB.glb'], styles: { backgroundColor: '#e3e5e6' }, camera: { p: [89.11, 42.08, 66.11], r: [-0.55, 0.82, 0.43] } },
    'AB09W0-049-TB': { models: ['AB09W0-037-TB.glb'], styles: { backgroundColor: '#e3e5e6' }, camera: { p: [89.11, 42.08, 66.11], r: [-0.55, 0.82, 0.43] } },
    'AB09W0-049-BB': { models: ['AB09W0-037-BB.glb'], styles: { backgroundColor: '#e3e5e6' }, camera: { p: [89.11, 42.08, 66.11], r: [-0.55, 0.82, 0.43] } },
    'AB09S1-045-TB': { models: ['AB09S1-037-TB.glb'], styles: { backgroundColor: '#a7a3a3' }, camera: { p: [89.11, 42.08, 66.11], r: [-0.55, 0.82, 0.43] } },
    'AB09S1-045-BB': { models: ['AB09S1-037-BB.glb'], styles: { backgroundColor: '#a7a3a3' }, camera: { p: [89.11, 42.08, 66.11], r: [-0.55, 0.82, 0.43] } },
    'AB09S1-049-TB': { models: ['AB09S1-037-TB.glb'], styles: { backgroundColor: '#a7a3a3' }, camera: { p: [89.11, 42.08, 66.11], r: [-0.55, 0.82, 0.43] } },
    'AB09S1-049-BB': { models: ['AB09S1-037-BB.glb'], styles: { backgroundColor: '#a7a3a3' }, camera: { p: [89.11, 42.08, 66.11], r: [-0.55, 0.82, 0.43] } },
    'AB09S2-045-TB': { models: ['AB09S2-037-TB.glb'], styles: { backgroundColor: '#a7a3a3' }, camera: { p: [89.11, 42.08, 66.11], r: [-0.55, 0.82, 0.43] } },
    'AB09S2-045-BB': { models: ['AB09S2-037-BB.glb'], styles: { backgroundColor: '#a7a3a3' }, camera: { p: [89.11, 42.08, 66.11], r: [-0.55, 0.82, 0.43] } },
    'AB09S2-049-TB': { models: ['AB09S2-037-TB.glb'], styles: { backgroundColor: '#a7a3a3' }, camera: { p: [89.11, 42.08, 66.11], r: [-0.55, 0.82, 0.43] } },
    'AB09S2-049-BB': { models: ['AB09S2-037-BB.glb'], styles: { backgroundColor: '#a7a3a3' }, camera: { p: [89.11, 42.08, 66.11], r: [-0.55, 0.82, 0.43] } },
    'AD09W0-045-TB': { models: ['AD09W0-037-TB.glb'], styles: { backgroundColor: '#e3e5e6' }, camera: { p: [89.11, 42.08, 66.11], r: [-0.55, 0.82, 0.43] } },
    'AD09W0-045-BB': { models: ['AD09W0-037-BB.glb'], styles: { backgroundColor: '#e3e5e6' }, camera: { p: [89.11, 42.08, 66.11], r: [-0.55, 0.82, 0.43] } },
    'AD09W0-049-TB': { models: ['AD09W0-037-TB.glb'], styles: { backgroundColor: '#e3e5e6' }, camera: { p: [89.11, 42.08, 66.11], r: [-0.55, 0.82, 0.43] } },
    'AD09W0-049-BB': { models: ['AD09W0-037-BB.glb'], styles: { backgroundColor: '#e3e5e6' }, camera: { p: [89.11, 42.08, 66.11], r: [-0.55, 0.82, 0.43] } },
    'AD09S1-037-TB': { models: ['AB09S1-037-TB.glb'], styles: { backgroundColor: '#a7a3a3' }, camera: { p: [89.11, 42.08, 66.11], r: [-0.55, 0.82, 0.43] } },
    'AD09S1-037-BB': { models: ['AB09S1-037-BB.glb'], styles: { backgroundColor: '#a7a3a3' }, camera: { p: [89.11, 42.08, 66.11], r: [-0.55, 0.82, 0.43] } },
    'AD09S1-045-TB': { models: ['AB09S1-037-TB.glb'], styles: { backgroundColor: '#a7a3a3' }, camera: { p: [89.11, 42.08, 66.11], r: [-0.55, 0.82, 0.43] } },
    'AD09S1-045-BB': { models: ['AB09S1-037-BB.glb'], styles: { backgroundColor: '#a7a3a3' }, camera: { p: [89.11, 42.08, 66.11], r: [-0.55, 0.82, 0.43] } },
    'AD09S1-049-TB': { models: ['AB09S1-037-TB.glb'], styles: { backgroundColor: '#a7a3a3' }, camera: { p: [89.11, 42.08, 66.11], r: [-0.55, 0.82, 0.43] } },
    'AD09S1-049-BB': { models: ['AB09S1-037-BB.glb'], styles: { backgroundColor: '#a7a3a3' }, camera: { p: [89.11, 42.08, 66.11], r: [-0.55, 0.82, 0.43] } },
    'AB09R0-037-TB': { models: ['AB09W0-037-TB.glb'], styles: { backgroundColor: '#e3e5e6' }, camera: { p: [89.11, 42.08, 66.11], r: [-0.55, 0.82, 0.43] } },
    'AB09R0-037-BB': { models: ['AB09W0-037-BB.glb'], styles: { backgroundColor: '#e3e5e6' }, camera: { p: [89.11, 42.08, 66.11], r: [-0.55, 0.82, 0.43] } },
    'AB09R0-045-TB': { models: ['AB09W0-037-TB.glb'], styles: { backgroundColor: '#e3e5e6' }, camera: { p: [89.11, 42.08, 66.11], r: [-0.55, 0.82, 0.43] } },
    'AB09R0-045-BB': { models: ['AB09W0-037-BB.glb'], styles: { backgroundColor: '#e3e5e6' }, camera: { p: [89.11, 42.08, 66.11], r: [-0.55, 0.82, 0.43] } },
    'AB09R0-049-TB': { models: ['AB09W0-037-TB.glb'], styles: { backgroundColor: '#e3e5e6' }, camera: { p: [89.11, 42.08, 66.11], r: [-0.55, 0.82, 0.43] } },
    'AB09R0-049-BB': { models: ['AB09W0-037-BB.glb'], styles: { backgroundColor: '#e3e5e6' }, camera: { p: [89.11, 42.08, 66.11], r: [-0.55, 0.82, 0.43] } },
};

// Умная функция получения параметров 3D-модели
const getModel3D = (brake, spring, height, bearing, noAbsorber) => {
    const fullCode = `${brake || 'XX'}09${spring || 'X'}-${height || '037'}-${bearing || 'TB'}`;
    if (noAbsorber && brake === 'AD' && spring === 'W0' && bearing === 'TB') {
        const noAbsorberCode = fullCode + '-noAbsorber';
        if (modelMap[noAbsorberCode]) return modelMap[noAbsorberCode];
    }
    if (modelMap[fullCode]) return modelMap[fullCode];
    if (height && height !== '037') {
        const fallbackCode = `${brake || 'XX'}09${spring || 'X'}-037-${bearing || 'TB'}`;
        if (modelMap[fallbackCode]) return modelMap[fallbackCode];
    }
    const possibleCodes = Object.keys(modelMap).filter(key => {
        const parts = key.split('-');
        if (parts.length !== 3) return false;
        const codeBrakeSpring = parts[0];
        const codeBearing = parts[2];
        return codeBrakeSpring === `${brake || 'XX'}09${spring || 'X'}` && codeBearing === (bearing || 'TB');
    });
    if (possibleCodes.length > 0) {
        return modelMap[possibleCodes[0]];
    }
    return modelMap['AB09W0-037-TB'] || null;
};

// ------------------------------------------------------------
// 2. 3D ВИЗУАЛИЗАЦИЯ (обновленная с кнопкой увеличения)
// ------------------------------------------------------------
function Model3DViewer({ modelData, onExpand }) {
    if (!modelData) return null;
    const { models, styles, camera } = modelData;
    const { backgroundColor } = styles || { backgroundColor: '#0A0E14' };
    const initialCameraPosition = camera?.p || [80, 80, 80];
    const initialCameraRotation = camera?.r || [0, 0, 0];
    const fileName = models[0]?.replace('.glb', '') || '3D модель';

    return (
        <div style={{ width: '100%', height: '200px', background: backgroundColor, borderRadius: '8px', overflow: 'hidden', position: 'relative' }}>
            <div style={{ position: 'absolute', top: 8, right: 12, color: 'rgba(255,255,255,0.7)', fontSize: 10, fontFamily: 'monospace', zIndex: 1, background: 'rgba(0,0,0,0.5)', padding: '2px 8px', borderRadius: 4 }}>
                3D-модель - {fileName}
            </div>
            {/* Кнопка увеличения */}
            <button
                onClick={onExpand}
                style={{
                    position: 'absolute',
                    bottom: 8,
                    right: 12,
                    zIndex: 2,
                    background: 'rgba(0,0,0,0.6)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '6px',
                    color: '#fff',
                    padding: '4px 8px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(34, 211, 238, 0.3)';
                    e.currentTarget.style.borderColor = '#22D3EE';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(0,0,0,0.6)';
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)';
                }}
            >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M8 3H5a2 2 0 0 0-2 2v3" />
                    <path d="M21 8V5a2 2 0 0 0-2-2h-3" />
                    <path d="M3 16v3a2 2 0 0 0 2 2h3" />
                    <path d="M16 21h3a2 2 0 0 0 2-2v-3" />
                </svg>
                Увеличить
            </button>
            <Canvas
                camera={{ position: initialCameraPosition, rotation: initialCameraRotation, fov: 50 }}
            >
                <ambientLight intensity={0.5} />
                <directionalLight position={[10, 10, 10]} intensity={2} />
                <directionalLight position={[-10, -10, -10]} intensity={1} />
                {models.map(url => (
                    <Model key={url} url={`/stl/${url}`} />
                ))}
                <OrbitControls enablePan={true} enableZoom={true} enableRotate={true} />
            </Canvas>
        </div>
    );
}

// ------------------------------------------------------------
// 2.1. Увеличенное модальное окно для 3D модели
// ------------------------------------------------------------
function Model3DModal({ modelData, isOpen, onClose }) {
    if (!isOpen || !modelData) return null;

    const { models, styles, camera } = modelData;
    const { backgroundColor } = styles || { backgroundColor: '#0A0E14' };
    const initialCameraPosition = camera?.p || [80, 80, 80];
    const initialCameraRotation = camera?.r || [0, 0, 0];


    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(0,0,0,0.8)',
                animation: 'fadeIn 0.3s ease',
            }}
            onClick={onClose}
        >
            <div
                style={{
                    position: 'relative',
                    width: '90vw',
                    maxWidth: '900px',
                    height: '80vh',
                    maxHeight: '700px',
                    background: backgroundColor,
                    borderRadius: '12px',
                    overflow: 'hidden',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.8)',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Кнопка закрытия */}
                <button
                    onClick={onClose}
                    style={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        zIndex: 10,
                        background: 'rgba(0,0,0,0.6)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: '50%',
                        color: '#fff',
                        width: '36px',
                        height: '36px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '20px',
                        transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(255,0,0,0.3)';
                        e.currentTarget.style.borderColor = '#ff4444';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(0,0,0,0.6)';
                        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)';
                    }}
                >
                    ✕
                </button>

                {/* Название модели */}
                <div
                    style={{
                        position: 'absolute',
                        top: 12,
                        left: 16,
                        zIndex: 10,
                        color: 'rgba(255,255,255,0.8)',
                        fontSize: '14px',
                        fontFamily: 'monospace',
                        background: 'rgba(0,0,0,0.5)',
                        padding: '4px 12px',
                        borderRadius: '4px',
                    }}
                >
                    {models[0]?.replace('.glb', '') || '3D модель'}
                </div>

                <Canvas
                    camera={{
                        position: initialCameraPosition,
                        rotation: initialCameraRotation,
                        fov: 40
                    }}
                    style={{ width: '100%', height: '100%' }}
                >
                    <ambientLight intensity={0.5} />
                    <directionalLight position={[10, 10, 10]} intensity={2} />
                    <directionalLight position={[-10, -10, -10]} intensity={1} />
                    {models.map(url => (
                        <Model key={url} url={`/stl/${url}`} />
                    ))}
                    <OrbitControls
                        enablePan={true}
                        enableZoom={true}
                        enableRotate={true}
                        minDistance={20}
                        maxDistance={300}
                    />
                </Canvas>

                {/* Подсказка по управлению */}
                <div
                    style={{
                        position: 'absolute',
                        bottom: 16,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 10,
                        color: 'rgba(255,255,255,0.4)',
                        fontSize: '11px',
                        fontFamily: 'monospace',
                        background: 'rgba(0,0,0,0.4)',
                        padding: '4px 16px',
                        borderRadius: '20px',
                        letterSpacing: '0.5px',
                    }}
                >
                    🖱 Вращайте · Масштабируйте · Панорамируйте
                </div>
            </div>

            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: scale(0.95); }
                    to { opacity: 1; transform: scale(1); }
                }
            `}</style>
        </div>
    );
}

// ------------------------------------------------------------
// 3. SVG-ВИЗУАЛИЗАЦИЯ
// ------------------------------------------------------------
function HubCrossSection({ absRing, sealType, hubType, theme }) {
    const isReinforced = hubType === 'reinforced';
    const hasAbs = absRing;
    const isHeavySeal = sealType === 'heavy';

    const isDark = theme === 'dark';

    const bgColor = isDark ? '#0A0E14' : '#f9fafb';
    const strokeColor = isDark ? '#1F2D42' : '#d1d5db';

    const hubFill = '#111722';
    const hubStroke = '#2A3D58';
    const accentColor = '#22D3EE';
    const sealColor = isHeavySeal ? '#10B981' : '#3D5270';
    const textColor = '#6B7E99';

    return (
        <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
            <circle cx="100" cy="100" r="88" fill={bgColor} stroke={strokeColor} strokeWidth="1.5" />
            {isReinforced && (
                <circle cx="100" cy="100" r="80" fill="none" stroke={accentColor} strokeWidth="1.5" strokeDasharray="4 3" opacity="0.6" />
            )}
            <circle cx="100" cy="100" r="62" fill="none" stroke={hubStroke} strokeWidth="2" strokeDasharray="6 4" />
            <circle cx="100" cy="100" r="44" fill="none" stroke={accentColor} strokeWidth="2.5" />
            <circle cx="100" cy="100" r="28" fill={hubFill} stroke={hubStroke} strokeWidth="1.5" />
            <circle cx="100" cy="100" r="16" fill="#0A0E14" stroke="#1F2D42" strokeWidth="1" />
            <circle cx="100" cy="100" r="6" fill="#1F2D42" />
            {hasAbs && (
                <circle cx="100" cy="100" r="70" fill="none" stroke="#F59E0B" strokeWidth="3" strokeDasharray="6 4" opacity="0.9" />
            )}
            <rect x="14" y="90" width="6" height="20" rx="2" fill={sealColor} />
            <rect x="180" y="90" width="6" height="20" rx="2" fill={sealColor} />
            <text x="100" y="105" textAnchor="middle" fill={textColor} fontSize="8" fontFamily="monospace">HUB</text>
            {isReinforced && (
                <text x="100" y="125" textAnchor="middle" fill={accentColor} fontSize="6" fontFamily="monospace">УСИЛЕННАЯ</text>
            )}
            {hasAbs && (
                <text x="100" y="145" textAnchor="middle" fill="#F59E0B" fontSize="6" fontFamily="monospace">ABS</text>
            )}
        </svg>
    );
}

function AxleVisualization({ brake, height, bearing, theme }) {
    let heightNum = 37;
    if (height) heightNum = parseInt(height, 10);
    const minH = 37, maxH = 49;
    const factor = (heightNum - minH) / (maxH - minH);
    const axleY = 80 + factor * 40;

    const isDisc = brake === 'AD';
    const isTB = bearing === 'TB';
    const isDark = theme === 'dark';

    const frameFill = isDark ? 'rgba(255,255,255,0.06)' : '#e5e7eb';
    const frameStroke = isDark ? 'rgba(255,255,255,0.15)' : '#9ca3af';
    const beamFill = isDark ? 'rgba(255,255,255,0.06)' : '#d1d5db';
    const beamStroke = isDark ? 'rgba(255,255,255,0.15)' : '#6b7280';
    const armStroke = isDark ? 'rgba(255,255,255,0.12)' : '#9ca3af';

    const hubFill = '#161c27';
    const hubInnerFill = 'rgba(255,255,255,0.04)';
    const discColor = isDisc ? 'rgba(245,158,11,0.15)' : 'rgba(255,255,255,0.15)';
    const bearingTextColor = isTB ? '#f59e0b' : '#60a5fa';

    const springFill = isDark ? 'rgba(245,158,11,0.1)' : 'rgba(245,158,11,0.2)';
    const springStroke = '#f59e0b';

    return (
        <svg viewBox="0 0 300 180" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
            <defs>
                <linearGradient id="frameGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.05)'} />
                    <stop offset="100%" stopColor={isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)'} />
                </linearGradient>
                <linearGradient id="hubGrad" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#2A3D58" />
                    <stop offset="100%" stopColor="#1F2D42" />
                </linearGradient>
            </defs>

            <rect x="30" y="18" width="240" height="14" rx="4" fill={frameFill} stroke={frameStroke} strokeWidth="1.2" />
            <rect x="30" y="18" width="240" height="2" fill="url(#frameGrad)" opacity="0.5" />

            <rect x="72" y="32" width="16" height={axleY - 36} rx="6" fill={springFill} stroke={springStroke} strokeWidth="1.2" />
            <rect x={300 - 72 - 16} y="32" width="16" height={axleY - 36} rx="6" fill={springFill} stroke={springStroke} strokeWidth="1.2" />

            <rect x="45" y={axleY} width="210" height="12" rx="3" fill={beamFill} stroke={beamStroke} strokeWidth="1.5" />
            <rect x="45" y={axleY} width="210" height="2" fill={isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'} />

            <path d={`M82 ${axleY + 6} L82 ${axleY + 42}`} stroke={armStroke} strokeWidth="6" strokeLinecap="round" />
            <path d={`M218 ${axleY + 6} L218 ${axleY + 42}`} stroke={armStroke} strokeWidth="6" strokeLinecap="round" />

            <circle cx="55" cy={axleY + 6} r="22" fill={hubFill} stroke="url(#hubGrad)" strokeWidth="2" />
            <circle cx="55" cy={axleY + 6} r="12" fill={hubInnerFill} stroke={isTB ? '#f59e0b' : '#60a5fa'} strokeWidth="2" />
            <circle cx="55" cy={axleY + 6} r="5" fill={discColor} />
            {isDisc && <circle cx="55" cy={axleY + 6} r="2" fill="#f59e0b" opacity="0.6" />}
            <text x="55" y={axleY + 6 + 4} textAnchor="middle" fill={bearingTextColor} fontSize="6" fontFamily="monospace" fontWeight="bold">{bearing || 'XX'}</text>

            <circle cx="245" cy={axleY + 6} r="22" fill={hubFill} stroke="url(#hubGrad)" strokeWidth="2" />
            <circle cx="245" cy={axleY + 6} r="12" fill={hubInnerFill} stroke={isTB ? '#f59e0b' : '#60a5fa'} strokeWidth="2" />
            <circle cx="245" cy={axleY + 6} r="5" fill={discColor} />
            {isDisc && <circle cx="245" cy={axleY + 6} r="2" fill="#f59e0b" opacity="0.6" />}
            <text x="245" y={axleY + 6 + 4} textAnchor="middle" fill={bearingTextColor} fontSize="6" fontFamily="monospace" fontWeight="bold">{bearing || 'XX'}</text>

            <line x1="280" y1="26" x2="280" y2={axleY + 6} stroke="#f59e0b" strokeWidth="1.2" strokeDasharray="4 3" opacity="0.4" />
            <line x1="274" y1="26" x2="286" y2="26" stroke="#f59e0b" strokeWidth="1.2" opacity="0.4" />
            <line x1="274" y1={axleY + 6} x2="286" y2={axleY + 6} stroke="#f59e0b" strokeWidth="1.2" opacity="0.4" />
            <text x="295" y={Math.max(42, axleY / 2 + 20)} fontFamily="monospace" fontSize="9" fill="#f59e0b" textAnchor="middle" transform={`rotate(-90 295 ${Math.max(42, axleY / 2 + 20)})`} opacity="0.7">{height || '—'}</text>
        </svg>
    );
}

// ------------------------------------------------------------
// 4. ОСНОВНОЙ КОМПОНЕНТ (5 шагов)
// ------------------------------------------------------------
export function ConfiguratorAxles() {
    const { theme } = useTheme();

    // Состояния
    const [step, setStep] = useState(1);
    const [brake, setBrake] = useState(null);
    const [spring, setSpring] = useState(null);
    const [height, setHeight] = useState(null);
    const [bearing, setBearing] = useState(null);
    const [noAbsorber, setNoAbsorber] = useState(false);
    const [quantity, setQuantity] = useState(1);

    const [hubType] = useState('standard');
    const [absRing, setAbsRing] = useState(true);
    const [sealType] = useState('standard');

    // Состояние для модального окна 3D
    const [is3DModalOpen, setIs3DModalOpen] = useState(false);

    // Форма
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        city: '',
        organization: '',
        comment: '',
    });
    const [inputFillCheck, setInputFillCheck] = useState(false);
    const [showAutorisationForm, setShowAutorisationForm] = useState(false);

    // Алерты
    const [loadManyRequest, setLoadManyRequest] = useState(false);
    const [showAlertNoCancel, setShowAlertNoCancel] = useState(false);
    const [alertNoCancelParams, setAlertNoCancelParams] = useState({
        tittle: 'Ошибка',
        buttonText: 'выйти',
        message: '',
        function: null,
        arguments: null,
    });

    // Города
    const [cityOptions, setCityOptions] = useState([]);
    const russianCities = ['Москва', 'Санкт-Петербург', 'Новосибирск', 'Екатеринбург', 'Казань'];

    // Вычисляемые данные
    const availableSprings = useMemo(() => {
        if (!brake) return [];
        return springTypes[brake] || [];
    }, [brake]);

    const availableHeights = useMemo(() => {
        if (!brake || !spring) return [];
        const entry = catalog[brake + '09']?.[spring];
        return entry ? entry.heights : [];
    }, [brake, spring]);

    const availableBearings = useMemo(() => {
        if (!brake || !spring || !height) return [];
        const entry = catalog[brake + '09']?.[spring];
        return entry ? entry.bearings : [];
    }, [brake, spring, height]);

    // Автовыбор
    useEffect(() => {
        if (brake) {
            const firstSpring = availableSprings[0] || null;
            setSpring(firstSpring);
            setHeight(null);
            setBearing(null);
        }
    }, [brake, availableSprings]);

    useEffect(() => {
        if (brake && spring) {
            const heights = availableHeights;
            if (heights.length > 0) {
                setHeight(heights[0]);
            } else {
                setHeight(null);
                setBearing(null);
            }
        }
    }, [brake, spring, availableHeights]);

    useEffect(() => {
        if (brake && spring && height) {
            const bearings = availableBearings;
            if (bearings.length > 0) {
                if (bearing && bearings.includes(bearing)) {
                    // оставляем
                } else {
                    setBearing(bearings[0]);
                }
            } else {
                setBearing(null);
            }
        }
    }, [brake, spring, height, availableBearings, bearing]);

    // Формирование кода модели
    const getModelName = () => {
        if (!brake || !spring || !height || !bearing) {
            const b = brake || 'XX';
            const s = spring || 'X';
            const h = height || 'XXX';
            const br = bearing || 'XX';
            return `${b}09${s}-${h}-${br}`;
        }
        return `${brake}09${spring}-${height}-${bearing}`;
    };

    const getDisplayName = () => getModelName();

    // Обработчики формы
    const handleFormChange = (e) => {
        const { name, value } = e.target;
        let processedValue = value;
        if (name === 'phone') {
            processedValue = correctNumber(value);
        }
        setFormData(prev => ({ ...prev, [name]: processedValue }));
        if (inputFillCheck) setInputFillCheck(false);
    };

    const handleCitySearch = (value) => {
        if (!value) {
            setCityOptions([]);
            return;
        }
        const filtered = russianCities.filter(city =>
            city.toLowerCase().includes(value.toLowerCase())
        );
        setCityOptions(filtered.map(city => ({ value: city })));
    };

    const handleCitySelect = (value) => {
        setFormData(prev => ({ ...prev, city: value }));
        if (inputFillCheck) setInputFillCheck(false);
        setCityOptions([]);
    };

    const isFieldInvalid = (fieldName) => {
        if (!inputFillCheck) return false;
        if (fieldName === 'phone') {
            const phoneClean = formData.phone.replace(/\D/g, '');
            return formData.phone === '' || phoneClean.length !== 11;
        }
        return formData[fieldName] === '';
    };

    const buildMessage = () => {
        const parts = [];
        const modelName = getModelName();
        parts.push(`Модель: ${modelName} - ${quantity} шт.`);
        parts.push(`Тормоза: ${brake === 'AB' ? 'Барабанные' : 'Дисковые'};`);
        parts.push(`Полурессора: ${springLabels[spring] || spring}`);
        parts.push(`Высота: ${height};`);
        parts.push(`Подшипник: ${bearingLabels[bearing] || bearing};`);
        if (noAbsorber) {
            parts.push('Без амортизатора;');
        } else {
            parts.push('С амортизатором;');
        }
        parts.push(`Ступица: ${hubType === 'reinforced' ? 'Усиленная' : 'Стандартная'};`);
        parts.push(`ABS: ${absRing ? 'Да' : 'Нет'};`);
        parts.push(`Уплотнения: ${sealType === 'heavy' ? 'Для тяжёлых условий' : 'Стандартные'};`);

        if (formData.organization && formData.organization.trim() !== '') {
            parts.push(`Организация: ${formData.organization}.;`);
        }
        if (formData.comment && formData.comment.trim() !== '') {
            parts.push(`Комментарий: ${formData.comment}.`);
        }
        return parts.join(' ');
    };

    const handleSubmitRequest = async () => {
        setInputFillCheck(true);
        const nameEmpty = formData.name.trim() === '';
        const cityEmpty = formData.city.trim() === '';
        const phoneClean = formData.phone.replace(/\D/g, '');
        const phoneInvalid = formData.phone === '' || phoneClean.length !== 11;

        if (nameEmpty || cityEmpty || phoneInvalid) {
            return;
        }

        const token = localStorage.getItem('token');
        if (!token) {
            setShowAutorisationForm(true);
            return;
        }

        defenseFromManyRequest(setLoadManyRequest, 2000);

        const messageText = buildMessage();
        const fields = {
            token: token,
            object: getModelName(),
            text: messageText,
            contactPhone: formData.phone,
            city: formData.city,
            name: formData.name,
        };

        try {
            let answer = await fetchRequestFeedback(fields);
            if (answer === `пользователь с таким токеном не найден\n`) {
                setAlertNoCancelParams({
                    tittle: 'Ошибка',
                    buttonText: 'выйти',
                    message: answer,
                    function: logout,
                    arguments: null,
                });
                setShowAlertNoCancel(true);
            } else if (answer === 'many request\n') {
                setAlertNoCancelParams({
                    tittle: 'Ошибка',
                    buttonText: 'продолжить',
                    message: 'Разрешено отправлять не более одного вопроса раз в 30 минут',
                    function: null,
                    arguments: null,
                });
                setShowAlertNoCancel(true);
            } else if (answer === 'ok') {
                setAlertNoCancelParams({
                    tittle: 'Сообщение',
                    buttonText: 'продолжить',
                    message: 'Ваш запрос успешно отправлен',
                    function: null,
                    arguments: null,
                });
                setShowAlertNoCancel(true);
                setFormData({ name: '', phone: '', city: '', organization: '', comment: '' });
            }
        } catch (error) {
            console.log('error', error);
            if (error === 'пользователь с таким токеном не найден') {
                setAlertNoCancelParams({
                    tittle: 'Ошибка',
                    buttonText: 'выйти',
                    message: error,
                    function: null,
                    arguments: null,
                });
                setShowAlertNoCancel(true);
            }
        }
    };

    // Навигация
    const goToNextStep = () => { if (step < 5) setStep(step + 1); };
    const goToPrevStep = () => { if (step > 1) setStep(step - 1); };

    const canAccessStep = (targetStep) => {
        if (targetStep === 1) return true;
        if (targetStep === 2) return brake !== null;
        if (targetStep === 3) return brake !== null && spring !== null;
        if (targetStep === 4) return brake !== null && spring !== null && height !== null && bearing !== null;
        if (targetStep === 5) return brake !== null && spring !== null && height !== null && bearing !== null;
        return false;
    };

    const goToStep = (targetStep) => {
        if (canAccessStep(targetStep)) setStep(targetStep);
    };

    // Рендеры шагов
    const renderStep1 = () => (
        <div className="left-content silos-fade-in">
            <h1 className="page-title">Выберите тип тормозов</h1>
            <p className="page-subtitle">Барабанные или дисковые</p>
            <div className="type-grid">
                {brakeSystems.map(b => (
                    <button
                        key={b.id}
                        onClick={() => setBrake(b.id)}
                        className={`type-card ${brake === b.id ? 'type-card-active' : ''}`}
                    >
                        <span className="type-label">{b.label}</span>
                        <span className="type-desc">{b.desc}</span>
                    </button>
                ))}
            </div>
        </div>
    );

    const renderStep2 = () => (
        <div className="left-content silos-fade-in">
            <h1 className="page-title">Выберите тип полурессоры</h1>
            <p className="page-subtitle">Доступные для выбранных тормозов</p>
            <div className="type-grid">
                {availableSprings.map(s => (
                    <button
                        key={s}
                        onClick={() => setSpring(s)}
                        className={`type-card ${spring === s ? 'type-card-active' : ''}`}
                    >
                        <span className="type-label">{springLabels[s]}</span>
                        <span className="type-desc">{springDesc[s]}</span>
                    </button>
                ))}
            </div>
        </div>
    );

    const renderStep3 = () => {
        if (availableHeights.length === 0) {
            return (
                <div className="left-content silos-fade-in">
                    <h1 className="page-title">Свойства</h1>
                    <p className="page-subtitle">Нет доступных комбинаций для выбранных параметров</p>
                    <button onClick={goToPrevStep} className="nav-btn nav-btn-back">Назад</button>
                </div>
            );
        }

        const handleHeightChange = (h) => {
            setHeight(h);
            const bearingsForNewHeight = catalog[brake + '09']?.[spring]?.bearings || [];
            if (bearing && bearingsForNewHeight.includes(bearing)) {
                // оставляем
            } else {
                setBearing(bearingsForNewHeight[0] || null);
            }
        };

        return (
            <div className="left-content silos-fade-in">
                <h1 className="page-title">Свойства</h1>
                <p className="page-subtitle">Выберите высоту подвески, тип подшипника и наличие амортизатора</p>

                <div className="param-group">
                    <label className="param-label">Высота</label>
                    <Segmented
                        options={availableHeights.map(h => ({ value: h, label: h }))}
                        value={height}
                        onChange={handleHeightChange}
                        className={`density-segmented ${theme}`}
                        block
                    />
                </div>

                <div className="param-group">
                    <label className="param-label">Тип подшипника</label>
                    <div className="type-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
                        {availableBearings.map(b => (
                            <button
                                key={b}
                                onClick={() => setBearing(b)}
                                className={`type-card ${bearing === b ? 'type-card-active' : ''}`}
                            >
                                <span className="type-label">{b}</span>
                                <span className="type-desc">{bearingDesc[b]}</span>
                            </button>
                        ))}
                    </div>
                </div>

                <div className="param-group">
                    <label className="param-label">Амортизатор</label>
                    <Segmented
                        options={[
                            { value: false, label: 'С амортизатором' },
                            { value: true, label: 'Без амортизатора' },
                        ]}
                        value={noAbsorber}
                        onChange={setNoAbsorber}
                        className={`density-segmented ${theme}`}
                        block
                    />
                </div>

                <div className="param-group quantity-group">
                    <label className="param-label">Количество осей</label>
                    <input
                        type="number"
                        min="1"
                        max="99"
                        value={quantity}
                        onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                        className="quantity-input"
                    />
                </div>
            </div>
        );
    };

    const renderStep4 = () => (
        <div className="left-content silos-fade-in">
            <h1 className="page-title">Ступица</h1>
            <p className="page-subtitle">Настройте тип ступицы, наличие кольца ABS и уплотнения</p>



            <div className="param-group">
                <label className="param-label">Кольцо ABS</label>
                <Segmented
                    options={[
                        { value: true, label: 'Установлено' },
                        { value: false, label: 'Не установлено' },
                    ]}
                    value={absRing}
                    onChange={setAbsRing}
                    className={`density-segmented ${theme}`}
                    block
                />
            </div>

        </div>
    );

    const renderStep5 = () => (
        <div className="left-content silos-fade-in">
            <h1 className="page-title">Заявка</h1>
            <p className="page-subtitle">Заполните контактные данные для получения коммерческого предложения</p>

            <div className="form-group">
                <label className="form-label">Имя</label>
                <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleFormChange}
                    className={`form-input ${isFieldInvalid('name') ? 'feedbackFormRequiredFill' : ''}`}
                    placeholder="Введите ваше имя"
                />
            </div>

            <div className="form-group">
                <label className="form-label">Телефон</label>
                <IMaskInput
                    mask="+7 (000) 000-00-00"
                    value={formData.phone}
                    onAccept={(value, mask) => {
                        const digits = mask.unmaskedValue;
                        if (digits.length === 0) {
                            setFormData(prev => ({ ...prev, phone: '' }));
                        } else {
                            setFormData(prev => ({ ...prev, phone: `+7${digits.slice(0, 10)}` }));
                        }
                        if (inputFillCheck) setInputFillCheck(false);
                    }}
                    placeholder="+7 (000) 000-00-00"
                    className={`form-input ${isFieldInvalid('phone') ? 'feedbackFormRequiredFill' : ''}`}
                />
            </div>

            <div className="form-group">
                <label className="form-label">Город</label>
                <AutoComplete
                    className={`form-autocomplete ${theme} ${isFieldInvalid('city') ? 'feedbackFormRequiredFill' : ''}`}
                    classNames={{ popup: { root: `autocomplete-dropdown ${theme}` } }}
                    value={formData.city}
                    options={cityOptions}
                    onSearch={handleCitySearch}
                    onSelect={handleCitySelect}
                    onChange={(value) => {
                        setFormData(prev => ({ ...prev, city: value }));
                        if (inputFillCheck) setInputFillCheck(false);
                    }}
                    placeholder="Введите ваш город"
                    notFoundContent="Город не найден"
                    filterOption={false}
                    allowClear
                />
            </div>

            <div className="form-group">
                <label className="form-label">Организация (необязательно)</label>
                <input
                    type="text"
                    name="organization"
                    value={formData.organization}
                    onChange={handleFormChange}
                    className="form-input"
                    placeholder="Название вашей организации"
                />
            </div>

            <div className="form-group">
                <label className="form-label">Комментарий к заявке (необязательно)</label>
                <textarea
                    name="comment"
                    value={formData.comment}
                    onChange={handleFormChange}
                    className="form-input form-textarea"
                    placeholder="Дополнительная информация, особые пожелания..."
                    rows="3"
                />
            </div>
        </div>
    );

    const renderNavButtons = () => {
        const isBackDisabled = step === 1;
        const isNextDisabled =
            (step === 1 && !brake) ||
            (step === 2 && !spring) ||
            (step === 3 && (!height || !bearing)) ||
            (step === 5 && false);

        const nextLabel = step === 5 ? 'Запросить КП' : 'Далее >';
        const progress = ((step - 1) / (5 - 1)) * 100;

        return (
            <div className="nav-buttons">
                <div className="progress-bar" style={{ width: `${progress}%` }}> </div>
                <div className="nav-left">
                    <button
                        className="nav-btn nav-btn-back"
                        disabled={isBackDisabled}
                        onClick={goToPrevStep}
                    >
                        Назад
                    </button>
                </div>
                <span className="step-indicator">Шаг {step} / 5</span>
                <div className="nav-right">
                    <button
                        className={`nav-btn nav-btn-next ${loadManyRequest ? 'noActiveButton' : ''}`}
                        disabled={isNextDisabled}
                        onClick={step === 5 ? handleSubmitRequest : goToNextStep}
                    >
                        {nextLabel}
                    </button>
                </div>
            </div>
        );
    };

    // Вычисляем данные для 3D-модели
    const model3DData = useMemo(() => {
        return getModel3D(brake, spring, height, bearing, noAbsorber);
    }, [brake, spring, height, bearing, noAbsorber]);

    return (
        <div className={`configurator-silos ${theme}`}>
            <div className={`complexBack ${theme}`}> </div>

            <AlertNoCancel
                showAlertConfirm={showAlertNoCancel}
                setShowAlertConfirm={setShowAlertNoCancel}
                alertConfirmParams={alertNoCancelParams}
            />

            {/* Модальное окно для 3D модели */}
            <Model3DModal
                modelData={model3DData}
                isOpen={is3DModalOpen}
                onClose={() => setIs3DModalOpen(false)}
            />

            <div className="dropdown-login" style={{ display: showAutorisationForm ? 'block' : 'none' }}>
                <div className="buttonLogin" onClick={authYandex}>Войти через Яндекс ID</div>
                <div className="buttonLogin" onClick={authGoogle}>Войти через Google ID</div>
            </div>
            <div onClick={() => setShowAutorisationForm(false)} className={`darkSpace ${showAutorisationForm ? 'darkSpaceVisible' : ''}`}> </div>

            <div className={`configurator-body ${theme}`}>
                <div className={`configurator-left ${theme}`}>
                    <div className={`configurator-steps ${theme}`}>
                        {[
                            { num: 1, label: 'Тормоза' },
                            { num: 2, label: 'Рессора' },
                            { num: 3, label: 'Свойства' },
                            { num: 4, label: 'Ступица' },
                            { num: 5, label: 'Заявка' },
                        ].map((s) => {
                            const isActive = step === s.num;
                            const isCompleted = s.num < step;
                            const isClickable = canAccessStep(s.num);
                            const displayNumber = isCompleted ? '✓' : s.num;

                            return (
                                <span
                                    key={s.num}
                                    className={`step ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''} ${isClickable ? 'clickable' : ''}`}
                                    onClick={() => isClickable && goToStep(s.num)}
                                >
                                    <span className="step-number">{displayNumber}</span> {s.label}
                                </span>
                            );
                        })}
                    </div>

                    <div className={`left-content-wrapper ${theme}`}>
                        {step === 1 && renderStep1()}
                        {step === 2 && renderStep2()}
                        {step === 3 && renderStep3()}
                        {step === 4 && renderStep4()}
                        {step === 5 && renderStep5()}
                    </div>

                    {renderNavButtons()}
                </div>

                <div className={`configurator-right ${theme}`}>
                    <div className={`viz-block ${theme}`}>
                        <div className="viz-title">{getDisplayName()}</div>
                        <div className={`viz-svg ${theme}`}>
                            <AxleVisualization
                                brake={brake}
                                height={height}
                                bearing={bearing}
                                theme={theme}
                            />
                        </div>
                    </div>
                    <div className={`viz-block ${theme}`} style={{ minHeight: '120px' }}>
                        <div className="viz-title">Ступица (разрез)</div>
                        <div className={`viz-svg ${theme}`} style={{ padding: '4px', minHeight: '100px' }}>
                            <HubCrossSection absRing={absRing} sealType={sealType} hubType={hubType} theme={theme} />
                        </div>
                    </div>
                    {/* 3D модель с кнопкой увеличения */}
                    {model3DData && !is3DModalOpen && (
                        <div className={`viz-block ${theme}`} style={{ minHeight: '120px' }}>
                            <div className="viz-title">3D-модель</div>

                            <Model3DViewer
                                key={model3DData.models[0]}
                                modelData={model3DData}
                                onExpand={() => setIs3DModalOpen(true)}
                            />
                        </div>
                    )}
                    <div className={`specs-block ${theme}`}>
                        <div className="specs-title">СПЕЦИФИКАЦИЯ</div>
                        <div className={`specs-scroll ${theme}`}>
                            <div className={`specs-grid ${theme}`}>
                                <div className="spec-item">
                                    <span className="spec-label">Код модели</span>
                                    <span className="spec-value">{getDisplayName()}</span>
                                </div>
                                <div className="spec-item">
                                    <span className="spec-label">Тормоза</span>
                                    <span className="spec-value">{brake === 'AB' ? 'Барабанные' : brake === 'AD' ? 'Дисковые' : '—'}</span>
                                </div>
                                <div className="spec-item">
                                    <span className="spec-label">Полурессора</span>
                                    <span className="spec-value">{springLabels[spring] || spring || '—'}</span>
                                </div>
                                <div className="spec-item">
                                    <span className="spec-label">Высота</span>
                                    <span className="spec-value">{height || '—'}</span>
                                </div>
                                <div className="spec-item">
                                    <span className="spec-label">Подшипник</span>
                                    <span className="spec-value">{bearingLabels[bearing] || bearing || '—'}</span>
                                </div>
                                <div className="spec-item">
                                    <span className="spec-label">Амортизатор</span>
                                    <span className="spec-value">{noAbsorber ? 'Отсутствует' : 'Установлен'}</span>
                                </div>
                                <div className="spec-item">
                                    <span className="spec-label">Ступица</span>
                                    <span className="spec-value">{hubType === 'reinforced' ? 'Усиленная' : 'Стандартная'}</span>
                                </div>
                                <div className="spec-item">
                                    <span className="spec-label">ABS</span>
                                    <span className="spec-value">{absRing ? 'Да' : 'Нет'}</span>
                                </div>
                                <div className="spec-item">
                                    <span className="spec-label">Уплотнения</span>
                                    <span className="spec-value">{sealType === 'heavy' ? 'Для тяжёлых условий' : 'Стандартные'}</span>
                                </div>
                                <div className="spec-item">
                                    <span className="spec-label">Количество</span>
                                    <span className="spec-value">{quantity} шт.</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}